import { Injectable, inject, signal } from '@angular/core';
import {
  Observable,
  ReplaySubject,
  catchError,
  finalize,
  map,
  shareReplay,
  tap,
  throwError
} from 'rxjs';
import { ApiClientService } from '../services/api-client.service';
import { StorageService } from '../services/storage.service';
import { ApplicationConfiguration } from '../models';

const GRANTED_POLICIES_KEY = 'auth_granted_policies';

/**
 * Loads ABP-style application configuration after authentication.
 * CPNotes: Single-flight load; protected routes wait on ready$ before evaluating permissions
 */
@Injectable({
  providedIn: 'root'
})
export class ApplicationConfigurationLoaderService {
  private readonly apiClient = inject(ApiClientService);
  private readonly storage = inject(StorageService);

  private readonly readySubject = new ReplaySubject<boolean>(1);
  private request$: Observable<void> | undefined;

  private readonly _ready = signal(false);
  private readonly _grantedPolicies = signal<Record<string, boolean>>({});

  readonly ready = this._ready.asReadonly();
  readonly grantedPolicies = this._grantedPolicies.asReadonly();
  readonly ready$ = this.readySubject.asObservable();

  constructor() {
    this.readySubject.next(false);
  }

  /**
   * Fetches application configuration and stores granted policies.
   * Concurrent callers share one in-flight request.
   */
  load(): Observable<void> {
    if (this.request$) {
      return this.request$;
    }

    this.request$ = this.apiClient
      .get<ApplicationConfiguration>('auth/application-configuration')
      .pipe(
        tap((configuration: ApplicationConfiguration) => {
          const policies = configuration?.auth?.grantedPolicies || {};
          this._grantedPolicies.set(policies);
          this.storage.setItem(GRANTED_POLICIES_KEY, policies);
          this._ready.set(true);
          this.readySubject.next(true);
        }),
        map(() => void 0),
        catchError(error => {
          this._ready.set(false);
          this.readySubject.next(false);
          return throwError(() => error);
        }),
        finalize(() => {
          this.request$ = undefined;
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );

    return this.request$;
  }

  /**
   * Restores cached policies from storage without marking ready until load() succeeds.
   */
  hydrateFromStorage(): void {
    const policies = this.storage.getItem<Record<string, boolean>>(GRANTED_POLICIES_KEY);
    if (policies) {
      this._grantedPolicies.set(policies);
    }
  }

  /**
   * Clears authorization configuration state.
   */
  clear(): void {
    this._grantedPolicies.set({});
    this._ready.set(false);
    this.readySubject.next(false);
    this.request$ = undefined;
    this.storage.removeItem(GRANTED_POLICIES_KEY);
  }

  /**
   * Checks whether a policy is granted.
   */
  hasPermission(permission: string): boolean {
    return !!this._grantedPolicies()[permission];
  }

  /**
   * Checks whether any of the policies are granted.
   */
  hasAnyPermission(...permissions: string[]): boolean {
    return permissions.some(p => this.hasPermission(p));
  }

  /**
   * Checks whether all of the policies are granted.
   */
  hasAllPermissions(...permissions: string[]): boolean {
    return permissions.every(p => this.hasPermission(p));
  }
}
